import { __ } from '@wordpress/i18n';
import { InspectorControls } from '@wordpress/block-editor';
import {
  BaseControl,
  SelectControl,
  ToggleControl,
  __experimentalToggleGroupControl as ToggleGroupControl,
  __experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from '@wordpress/components';

import PanelBody from '../../../../components/PanelBody';
import ColorPicker from '../../../../components/ColorPicker';
import Slider from '../../../../components/Slider';

/**
 * Labelled colour swatch. ColorPicker renders only the swatch, so the label and
 * the gap below it would otherwise be repeated at all four call sites.
 */
function ColorRow({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <BaseControl.VisualLabel>{label}</BaseControl.VisualLabel>
      <ColorPicker color={value} onChange={onChange} />
    </div>
  );
}

export default function AllInspectorControls({ attributes, setAttributes }) {
  const {
    iconSize,
    shape,
    colorMode,
    iconColor,
    backgroundColor,
    hoverIconColor,
    hoverBackgroundColor,
    iconWeight,
    gap,
    justifyContent,
    openInNewTab,
    showLabels,
  } = attributes;

  return (
    <InspectorControls>
      <PanelBody title={__('Layout', 'brandy-blocks')} initialOpen>
        <ToggleGroupControl
          label={__('Shape', 'brandy-blocks')}
          value={shape}
          onChange={(value) => setAttributes({ shape: value })}
          isBlock
          __nextHasNoMarginBottom
        >
          <ToggleGroupControlOption value="none" label={__('None', 'brandy-blocks')} />
          <ToggleGroupControlOption value="rounded" label={__('Rounded', 'brandy-blocks')} />
          <ToggleGroupControlOption value="circle" label={__('Circle', 'brandy-blocks')} />
          <ToggleGroupControlOption value="square" label={__('Square', 'brandy-blocks')} />
        </ToggleGroupControl>

        <ToggleGroupControl
          label={__('Alignment', 'brandy-blocks')}
          value={justifyContent}
          onChange={(value) => setAttributes({ justifyContent: value })}
          isBlock
          __nextHasNoMarginBottom
        >
          <ToggleGroupControlOption value="left" label={__('Left', 'brandy-blocks')} />
          <ToggleGroupControlOption value="center" label={__('Center', 'brandy-blocks')} />
          <ToggleGroupControlOption value="right" label={__('Right', 'brandy-blocks')} />
        </ToggleGroupControl>

        <Slider
          label={__('Icon size (px)', 'brandy-blocks')}
          value={iconSize}
          onChange={(value) => setAttributes({ iconSize: value })}
          min={12}
          max={64}
        />

        <Slider
          label={__('Gap (px)', 'brandy-blocks')}
          value={gap}
          onChange={(value) => setAttributes({ gap: value })}
          min={0}
          max={48}
        />

        <SelectControl
          label={__('Icon weight', 'brandy-blocks')}
          value={iconWeight}
          options={[
            { value: 'regular', label: __('Regular', 'brandy-blocks') },
            { value: 'fill', label: __('Fill', 'brandy-blocks') },
            { value: 'duotone', label: __('Duotone', 'brandy-blocks') },
          ]}
          onChange={(value) => setAttributes({ iconWeight: value })}
          __nextHasNoMarginBottom
        />
      </PanelBody>

      <PanelBody title={__('Colors', 'brandy-blocks')} initialOpen={false}>
        <ToggleGroupControl
          label={__('Color mode', 'brandy-blocks')}
          value={colorMode}
          onChange={(value) => setAttributes({ colorMode: value })}
          isBlock
          __nextHasNoMarginBottom
          help={__(
            'Brand uses each platform’s official color. Inherit follows the surrounding text color.',
            'brandy-blocks'
          )}
        >
          <ToggleGroupControlOption value="brand" label={__('Brand', 'brandy-blocks')} />
          <ToggleGroupControlOption value="custom" label={__('Custom', 'brandy-blocks')} />
          <ToggleGroupControlOption value="inherit" label={__('Inherit', 'brandy-blocks')} />
        </ToggleGroupControl>

        {colorMode === 'custom' && (
          <>
            <ColorRow
              label={__('Icon color', 'brandy-blocks')}
              value={iconColor}
              onChange={(value) => setAttributes({ iconColor: value })}
            />
            <ColorRow
              label={__('Background color', 'brandy-blocks')}
              value={backgroundColor}
              onChange={(value) => setAttributes({ backgroundColor: value })}
            />
          </>
        )}

        <ColorRow
          label={__('Hover icon color', 'brandy-blocks')}
          value={hoverIconColor}
          onChange={(value) => setAttributes({ hoverIconColor: value })}
        />
        <ColorRow
          label={__('Hover background color', 'brandy-blocks')}
          value={hoverBackgroundColor}
          onChange={(value) => setAttributes({ hoverBackgroundColor: value })}
        />
      </PanelBody>

      <PanelBody title={__('Behavior', 'brandy-blocks')} initialOpen={false}>
        <ToggleControl
          label={__('Show labels', 'brandy-blocks')}
          checked={showLabels}
          onChange={(value) => setAttributes({ showLabels: value })}
          help={__('Display the platform name next to each icon.', 'brandy-blocks')}
          __nextHasNoMarginBottom
        />
        <ToggleControl
          label={__('Open in new tab', 'brandy-blocks')}
          checked={openInNewTab}
          onChange={(value) => setAttributes({ openInNewTab: value })}
          __nextHasNoMarginBottom
        />
      </PanelBody>
    </InspectorControls>
  );
}
